/**
 * The report's rich text: the one format a section's summary and a text block
 * are stored in, and how it is drawn, typed into and read back out.
 *
 * A SUBSET OF MARKDOWN, kept in the same strings the text always lived in:
 *
 *   **bold**   _italic_ (or *italic*)   [label](https://…)
 *   - a bullet            1. a numbered line
 *
 * Nothing else: no headings (a heading is a text block of its own), tables,
 * quotes, code, images or HTML. A literal `*`, `[`, `]`, `\` — and a `_` at the
 * edge of a word — is escaped with a backslash, and so is a line of text that
 * would otherwise read as a list item.
 *
 * WHY MARKDOWN AND NOT A DOCUMENT OF JSON. The text already lives in strings — a
 * widget override's `text`, a section's generated description — that Ana, the
 * settings form and the server all read and write, and plain text is valid in
 * this subset as it stands: every summary written before it reads exactly as
 * it did, and no report is migrated. A model also reads and writes markdown
 * natively, so the slide-edit agent keeps a summary's bold words and lists by
 * being told the subset in a few lines, where a JSON document would have been a
 * second field for it to keep in step with the first.
 *
 * NEVER HTML. Stored text is parsed here and drawn as DOM nodes — text,
 * `strong`, `em`, lists, and an `a` only for a link to the web or an email
 * address — and is never assigned as markup. The share page draws it for
 * anyone with the link, which is why the link rule is applied here, when
 * drawing, as well as by the server when storing (`rich_text.py`). What the
 * editor reads back out of a field is serialized the same way: the elements it
 * knows become the subset, and everything else becomes its text.
 *
 * Emphasis follows CommonMark's delimiter rules, so a star with a space after it
 * ("5* rating") stays a star, `snake_case` stays one word, and `***both***` or
 * `re**brand**ing` read as any markdown reader would read them. The serializer
 * writes italic with `_` so that it never runs into the stars of a bold beside
 * it — `**a_b_**_c_` is unambiguous where `**a*b***c*` is not — and with `*`
 * inside a word, where `_` cannot open.
 */

import { markedRuns } from './report-quoted-figures';

export type RichInline =
	| { kind: 'text'; text: string }
	| { kind: 'bold' | 'italic'; children: RichInline[] }
	| { kind: 'link'; href: string; children: RichInline[] };

export type RichBlock =
	| { kind: 'lines'; children: RichInline[] }
	| { kind: 'list'; ordered: boolean; start: number; items: RichInline[][] };

const BULLET_LINE = /^- (.*)$/;
/** Up to three digits: "2024. A year of growth" is a sentence, not a list. */
const NUMBERED_LINE = /^(\d{1,3})\. (.*)$/;
const ESCAPABLE = new Set(['\\', '*', '_', '[', ']', '(', ')', '-', '.']);

/** Where a link may point: the web, or an email address. `rich_text.py` too. */
const SAFE_LINK = /^(?:https?:\/\/|mailto:)[^\s<>()\\]+$/i;

/**
 * The link as it may be drawn, or null when it may not be.
 *
 * Spaces and parentheses are percent-encoded rather than refused: a reader
 * pastes Wikipedia links, and a parenthesis left in one would end the link
 * where its `](…)` closes. Anything but the web or an email address —
 * `javascript:`, `data:`, a relative path — is refused.
 */
export function safeLinkHref(url: string | null | undefined): string | null {
	const encoded = (url ?? '')
		.trim()
		.replace(/ /g, '%20')
		.replace(/\(/g, '%28')
		.replace(/\)/g, '%29');
	return SAFE_LINK.test(encoded) ? encoded : null;
}

/**
 * A link as a reader types it, made into one the subset keeps: "lolly.com"
 * becomes https://lolly.com and "press@lolly.com" a mailto. Null when it is
 * neither a web address nor an email address.
 */
export function typedLinkHref(typed: string): string | null {
	const value = typed.trim();
	if (/^[a-z][a-z0-9+.-]*:/i.test(value)) {
		return safeLinkHref(value);
	}
	if (/^[^\s@/]+@[^\s@/]+\.[a-z]{2,}$/i.test(value)) {
		return safeLinkHref(`mailto:${value}`);
	}
	if (/^[^\s@/]+\.[a-z]{2,}(?:[/?#]\S*)?$/i.test(value)) {
		return safeLinkHref(`https://${value}`);
	}
	return null;
}

// ── reading the stored text ──────────────────────────────────────────────────

export function parseRichText(source: string | null | undefined): RichBlock[] {
	const blocks: RichBlock[] = [];
	let lines: string[] = [];
	const flushLines = (): void => {
		if (lines.length) {
			blocks.push({
				kind: 'lines',
				children: parseInline(lines.join('\n'))
			});
			lines = [];
		}
	};

	for (const line of (source ?? '').split('\n')) {
		const bullet = BULLET_LINE.exec(line);
		const numbered = bullet ? null : NUMBERED_LINE.exec(line);
		if (!bullet && !numbered) {
			lines.push(line);
			continue;
		}
		const ordered = !bullet;
		const item = parseInline(bullet ? bullet[1] : numbered![2]);
		const last = blocks[blocks.length - 1];
		if (
			!lines.length &&
			last?.kind === 'list' &&
			last.ordered === ordered
		) {
			last.items.push(item);
			continue;
		}
		flushLines();
		blocks.push({
			kind: 'list',
			ordered,
			start: numbered ? Number(numbered[1]) : 1,
			items: [item]
		});
	}
	flushLines();
	return blocks;
}

/** What the reader sees of the text, markup aside — a list item per line. */
export function richTextToPlain(source: string | null | undefined): string {
	return parseRichText(source)
		.map(block =>
			block.kind === 'lines'
				? inlineText(block.children)
				: block.items.map(inlineText).join('\n')
		)
		.join('\n');
}

function inlineText(nodes: RichInline[]): string {
	return nodes
		.map(node =>
			node.kind === 'text' ? node.text : inlineText(node.children)
		)
		.join('');
}

/** A run of `*` or `_` that may open or close emphasis, per CommonMark. */
interface DelimiterRun {
	kind: 'run';
	char: '*' | '_';
	/** What is left of it to match. */
	count: number;
	/** How long it was, for the rule of three. */
	length: number;
	canOpen: boolean;
	canClose: boolean;
}

type InlinePart = RichInline | DelimiterRun;

function parseInline(source: string, links = true): RichInline[] {
	const parts: InlinePart[] = [];
	let text = '';
	const flushText = (): void => {
		if (text) {
			parts.push({ kind: 'text', text });
			text = '';
		}
	};

	let at = 0;
	while (at < source.length) {
		const char = source[at];
		if (char === '\\' && ESCAPABLE.has(source[at + 1])) {
			text += source[at + 1];
			at += 2;
			continue;
		}
		if (char === '*' || char === '_') {
			let end = at;
			while (source[end] === char) end++;
			const run = delimiterRun(source, at, end, char);
			// Only a single underscore is emphasis: "__init__" stays a name.
			if (run) {
				flushText();
				parts.push(run);
			} else {
				text += source.slice(at, end);
			}
			at = end;
			continue;
		}
		if (char === '[' && links) {
			const link = readLink(source, at);
			if (link) {
				flushText();
				parts.push({
					kind: 'link',
					href: link.href,
					children: parseInline(link.label, false)
				});
				at = link.end;
				continue;
			}
		}
		text += char;
		at++;
	}
	flushText();

	matchEmphasis(parts);
	return asInline(parts);
}

function delimiterRun(
	source: string,
	start: number,
	end: number,
	char: '*' | '_'
): DelimiterRun | null {
	if (char === '_' && end - start !== 1) {
		return null;
	}
	// Start and end of the text count as whitespace.
	const before = start > 0 ? source[start - 1] : ' ';
	const after = end < source.length ? source[end] : ' ';
	const left =
		!isSpace(after) &&
		(!isPunctuation(after) || isSpace(before) || isPunctuation(before));
	const right =
		!isSpace(before) &&
		(!isPunctuation(before) || isSpace(after) || isPunctuation(after));
	return {
		kind: 'run',
		char,
		count: end - start,
		length: end - start,
		// An underscore inside a word neither opens nor closes: snake_case.
		canOpen:
			char === '*' ? left : left && (!right || isPunctuation(before)),
		canClose:
			char === '*' ? right : right && (!left || isPunctuation(after))
	};
}

function isSpace(char: string): boolean {
	return /\s/.test(char);
}

function isPunctuation(char: string): boolean {
	return /[\p{P}\p{S}]/u.test(char);
}

/**
 * `[label](target)`, when the target is one a link may have. Anything else —
 * no closing bracket, a target nobody should follow — is not a link, and its
 * characters are read as text.
 */
function readLink(
	source: string,
	start: number
): { label: string; href: string; end: number } | null {
	let at = start + 1;
	while (at < source.length && source[at] !== ']') {
		if (source[at] === '\\') {
			at += 2;
			continue;
		}
		if (source[at] === '[' || source[at] === '\n') {
			return null;
		}
		at++;
	}
	if (at === start + 1 || source[at] !== ']' || source[at + 1] !== '(') {
		return null;
	}
	const close = source.indexOf(')', at + 2);
	const href = close < 0 ? null : safeLinkHref(source.slice(at + 2, close));
	return href
		? { label: source.slice(start + 1, at), href, end: close + 1 }
		: null;
}

/**
 * CommonMark's "process emphasis": each closer, nearest first, takes the
 * nearest opener of the same character that the rule of three allows, as bold
 * when both have two to spare and as italic otherwise. What is left over is
 * text.
 */
function matchEmphasis(parts: InlinePart[]): void {
	let closerAt = 0;
	while (closerAt < parts.length) {
		const closer = parts[closerAt];
		if (!isRun(closer) || !closer.canClose) {
			closerAt++;
			continue;
		}
		let openerAt = closerAt - 1;
		for (; openerAt >= 0; openerAt--) {
			const opener = parts[openerAt];
			if (
				isRun(opener) &&
				opener.char === closer.char &&
				opener.canOpen &&
				!ruleOfThree(opener, closer)
			) {
				break;
			}
		}
		if (openerAt < 0) {
			closerAt++;
			continue;
		}

		const opener = parts[openerAt] as DelimiterRun;
		const bold = opener.count >= 2 && closer.count >= 2;
		opener.count -= bold ? 2 : 1;
		closer.count -= bold ? 2 : 1;
		const emphasis: RichInline = {
			kind: bold ? 'bold' : 'italic',
			children: asInline(parts.slice(openerAt + 1, closerAt))
		};
		parts.splice(openerAt + 1, closerAt - openerAt - 1, emphasis);
		closerAt = openerAt + 2;
		if (opener.count === 0) {
			parts.splice(openerAt, 1);
			closerAt--;
		}
		if (closer.count === 0) {
			parts.splice(closerAt, 1);
		}
	}
}

function isRun(part: InlinePart): part is DelimiterRun {
	return part.kind === 'run';
}

function ruleOfThree(opener: DelimiterRun, closer: DelimiterRun): boolean {
	return (
		(opener.canClose || closer.canOpen) &&
		(opener.length + closer.length) % 3 === 0 &&
		!(opener.length % 3 === 0 && closer.length % 3 === 0)
	);
}

/** Unmatched runs back into text, and neighbouring text into one node. */
function asInline(parts: InlinePart[]): RichInline[] {
	const nodes: RichInline[] = [];
	for (const part of parts) {
		const node: RichInline = isRun(part)
			? { kind: 'text', text: part.char.repeat(part.count) }
			: part;
		if (node.kind === 'text') {
			if (!node.text) continue;
			const last = nodes[nodes.length - 1];
			if (last?.kind === 'text') {
				nodes[nodes.length - 1] = {
					kind: 'text',
					text: last.text + node.text
				};
				continue;
			}
		}
		nodes.push(node);
	}
	return nodes;
}

// ── drawing it ───────────────────────────────────────────────────────────────

/**
 * Draw `source` into `target`, replacing what it held. DOM nodes only, never
 * markup: text is a text node however it is written, so nothing in stored text
 * can become an element this function did not make. Line breaks are kept as
 * characters, for the container's `white-space: pre-line` to show — which is
 * exactly how plain text was drawn before it could be anything else.
 */
export function renderRichText(
	target: HTMLElement,
	source: string | null | undefined,
	marks: ReadonlySet<string> = NO_MARKED_NUMBERS
): void {
	const doc = target.ownerDocument;
	while (target.firstChild) {
		target.removeChild(target.firstChild);
	}
	for (const block of parseRichText(source)) {
		if (block.kind === 'lines') {
			appendInline(doc, target, block.children, marks);
			continue;
		}
		const list = doc.createElement(block.ordered ? 'ol' : 'ul');
		if (block.ordered && block.start !== 1) {
			list.setAttribute('start', String(block.start));
		}
		for (const item of block.items) {
			const entry = doc.createElement('li');
			appendInline(doc, entry, item, marks);
			list.appendChild(entry);
		}
		target.appendChild(list);
	}
}

const NO_MARKED_NUMBERS: ReadonlySet<string> = new Set();

/**
 * The class a number is drawn in when the live data has moved on from it —
 * in the editor only, which is the one surface that passes `marks`. A mark is
 * read back out of a field as its text (`serializeRichText`).
 */
export const STALE_FIGURE_CLASS = 'report-stale-figure';

function appendInline(
	doc: Document,
	parent: Node,
	nodes: RichInline[],
	marks: ReadonlySet<string>
): void {
	for (const node of nodes) {
		if (node.kind === 'text') {
			appendText(doc, parent, node.text, marks);
			continue;
		}
		const element = doc.createElement(
			node.kind === 'bold'
				? 'strong'
				: node.kind === 'italic'
				? 'em'
				: 'a'
		);
		if (node.kind === 'link') {
			// Checked again on the way out: `href` came through `safeLinkHref`
			// when it was parsed, and this is the line that makes it live.
			const href = safeLinkHref(node.href);
			if (!href) {
				appendInline(doc, parent, node.children, marks);
				continue;
			}
			element.setAttribute('href', href);
			element.setAttribute('target', '_blank');
			element.setAttribute('rel', 'noopener noreferrer nofollow');
		}
		appendInline(doc, element, node.children, marks);
		parent.appendChild(element);
	}
}

function appendText(
	doc: Document,
	parent: Node,
	text: string,
	marks: ReadonlySet<string>
): void {
	for (const run of markedRuns(text, marks)) {
		if (!run.marked) {
			parent.appendChild(doc.createTextNode(run.text));
			continue;
		}
		const mark = doc.createElement('mark');
		mark.className = STALE_FIGURE_CLASS;
		mark.appendChild(doc.createTextNode(run.text));
		parent.appendChild(mark);
	}
}

// ── reading it back out of a field ──────────────────────────────────────────

interface Marks {
	bold: boolean;
	italic: boolean;
	href: string | null;
}

interface Run extends Marks {
	text: string;
}

interface Line {
	item: { ordered: boolean; number: number } | null;
	runs: Run[];
}

const NO_MARKS: Marks = { bold: false, italic: false, href: null };
const BLOCK_ELEMENTS = new Set([
	'div',
	'p',
	'li',
	'blockquote',
	'pre',
	'h1',
	'h2',
	'h3',
	'h4',
	'h5',
	'h6'
]);

/**
 * What a field holds, in the subset — the reverse of `renderRichText`.
 *
 * The browser edits the field as it likes: `<b>` or a bold span for bold, a
 * `<div>` or a `<br>` per line, lists nested inside lists. Lines are read the
 * way `innerText` reads them, and only what the subset has survives: bold,
 * italic, links to the web or an email address, and one level of list. A
 * nested list is flattened into the one it sits in, an empty list item — what
 * ending a list with Enter leaves — is dropped, and everything else is its
 * text.
 */
export function serializeRichText(root: Node): string {
	const lines: Line[] = [];
	// The line text is going into; null when the next text starts a new one.
	let open: Line | null = null;
	// The list item being read, which every line started inside it belongs to.
	let item: Line['item'] = null;

	const endLine = (): void => {
		open = null;
	};
	// A <br>, or a newline in a text node: the end of a line — or, on a line
	// that has nothing on it yet, an empty line of its own.
	const breakLine = (): void => {
		if (!open) {
			lines.push({ item, runs: [] });
		}
		open = null;
	};
	const addText = (text: string, marks: Marks): void => {
		if (!open) {
			open = { item, runs: [] };
			lines.push(open);
		}
		const runs: Run[] = open.runs;
		const last = runs[runs.length - 1];
		if (last && sameMarks(last, marks)) {
			last.text += text;
		} else {
			runs.push({ ...marks, text });
		}
	};

	const walk = (node: Node, marks: Marks): void => {
		if (node.nodeType === TEXT_NODE) {
			(node.nodeValue ?? '')
				.replace(/ /g, ' ')
				.split('\n')
				.forEach((part, index) => {
					if (index > 0) breakLine();
					if (part) addText(part, marks);
				});
			return;
		}
		if (node.nodeType !== ELEMENT_NODE) {
			return;
		}
		const element = node as Element;
		const tag = element.nodeName.toLowerCase();
		if (tag === 'br') {
			breakLine();
			return;
		}
		if (tag === 'ul' || tag === 'ol') {
			const outer = item;
			const ordered = tag === 'ol';
			let number = ordered
				? Number(element.getAttribute('start')) || 1
				: 1;
			endLine();
			for (const child of Array.from(element.childNodes)) {
				item = { ordered, number: number++ };
				endLine();
				walk(child, marks);
				endLine();
			}
			item = outer;
			return;
		}
		const block = BLOCK_ELEMENTS.has(tag);
		const inner = marksOf(element, tag, marks);
		if (block && open && (open as Line).runs.length) endLine();
		for (const child of Array.from(element.childNodes)) {
			walk(child, inner);
		}
		if (block) endLine();
	};

	for (const child of Array.from(root.childNodes)) {
		walk(child, NO_MARKS);
	}
	return writeLines(lines);
}

const TEXT_NODE = 3;
const ELEMENT_NODE = 1;

function sameMarks(left: Marks, right: Marks): boolean {
	return (
		left.bold === right.bold &&
		left.italic === right.italic &&
		left.href === right.href
	);
}

function marksOf(element: Element, tag: string, marks: Marks): Marks {
	const style = (element as HTMLElement).style;
	return {
		bold: weightOf(tag, style?.fontWeight, marks.bold),
		italic: slantOf(tag, style?.fontStyle, marks.italic),
		href:
			tag === 'a'
				? safeLinkHref(element.getAttribute('href')) ?? marks.href
				: marks.href
	};
}

/** Bold by its tag, or by the style a browser un-bolding it may leave. */
function weightOf(
	tag: string,
	weight: string | undefined,
	inherited: boolean
): boolean {
	if (tag === 'b' || tag === 'strong') return true;
	if (!weight) return inherited;
	if (weight === 'bold' || weight === 'bolder') return true;
	if (weight === 'normal' || weight === 'lighter') return false;
	const numeric = Number(weight);
	return Number.isFinite(numeric) ? numeric >= 600 : inherited;
}

function slantOf(
	tag: string,
	slant: string | undefined,
	inherited: boolean
): boolean {
	if (tag === 'i' || tag === 'em') return true;
	if (slant === 'italic' || slant === 'oblique') return true;
	if (slant === 'normal') return false;
	return inherited;
}

function writeLines(lines: Line[]): string {
	const written: string[] = [];
	let previous: Line | null = null;
	let nextNumber = 1;
	for (const line of lines) {
		const text = writeInline(line.runs)
			.replace(/[ \t]+/g, ' ')
			.trim();
		if (line.item && !text) {
			continue;
		}
		if (!line.item) {
			written.push(escapeListMarker(text));
		} else if (line.item.ordered) {
			const number: number = previous?.item?.ordered
				? nextNumber
				: line.item.number;
			written.push(`${number}. ${text}`);
			nextNumber = number + 1;
		} else {
			written.push(`- ${text}`);
		}
		previous = line;
	}
	return written
		.join('\n')
		.replace(/\n{3,}/g, '\n\n')
		.trim();
}

/** A line of text that would read back as a list item, kept as text. */
function escapeListMarker(text: string): string {
	if (BULLET_LINE.test(text)) {
		return `\\${text}`;
	}
	return text.replace(/^(\d{1,3})\. /, '$1\\. ');
}

/**
 * One line's runs as markdown. Marks open and close as the runs change,
 * innermost closing first, and whitespace at the edge of a mark is moved
 * outside it — `**a **b` could not close, `**a** b` can. A link closes every
 * mark around it and carries its label's own marks inside: `**see** [**it**](…)`
 * reads the same as the bold link the reader made, and cannot be misread.
 */
function writeInline(runs: Run[]): string {
	let out = '';
	let index = 0;
	while (index < runs.length) {
		const href = runs[index].href;
		let end = index;
		while (end < runs.length && runs[end].href === href) end++;
		const segment = runs.slice(index, end);
		if (!href) {
			out += writeMarked(
				segment,
				out.slice(-1),
				runs[end]?.text[0] ?? ''
			);
		} else {
			const label = writeMarked(
				segment.map(run => ({ ...run, href: null })),
				'',
				''
			).trim();
			if (label) {
				const lead = /^\s/.test(segment[0].text) ? ' ' : '';
				const trail = /\s$/.test(segment[segment.length - 1].text)
					? ' '
					: '';
				out += `${lead}[${label}](${href})${trail}`;
			}
		}
		index = end;
	}
	return out;
}

function writeMarked(runs: Run[], before: string, after: string): string {
	let out = '';
	const openMarks: Array<'bold' | 'italic'> = [];
	let italicMark = '_';
	let pending = '';

	runs.forEach((run, at) => {
		const lead = /^\s*/.exec(run.text)![0];
		const core = run.text.slice(lead.length).replace(/\s*$/, '');
		const trail = run.text.slice(lead.length + core.length);
		pending += lead;
		if (!core) {
			pending += trail;
			return;
		}

		const wanted = { bold: run.bold, italic: run.italic };
		// Close down to the last mark this run still wants.
		while (openMarks.some(mark => !wanted[mark])) {
			const mark = openMarks.pop()!;
			out += mark === 'bold' ? '**' : italicMark;
		}
		out += pending;
		pending = '';
		for (const mark of ['bold', 'italic'] as const) {
			if (wanted[mark] && !openMarks.includes(mark)) {
				if (mark === 'italic') {
					// `_` cannot open or close inside a word; `*` can.
					const outside = [
						(out || before).slice(-1),
						italicNeighbour(runs, at, after)
					];
					italicMark = outside.some(char =>
						/[\p{L}\p{N}]/u.test(char)
					)
						? '*'
						: '_';
				}
				out += mark === 'bold' ? '**' : italicMark;
				openMarks.push(mark);
			}
		}
		out += escapeText(core);
		pending = trail;
	});

	while (openMarks.length) {
		const mark = openMarks.pop()!;
		out += mark === 'bold' ? '**' : italicMark;
	}
	return out + pending;
}

/** The character just after the italic stretch that starts at `from`. */
function italicNeighbour(runs: Run[], from: number, after: string): string {
	let at = from;
	while (at < runs.length && runs[at].italic) at++;
	return at < runs.length ? runs[at].text[0] ?? '' : after;
}

/**
 * Text as text: every character the subset reads as markup is escaped. An
 * underscore inside a word is left alone — it cannot be emphasis there, and
 * `brand\_name` would be noise in every text Ana reads back.
 */
function escapeText(text: string): string {
	return text.replace(/[\\*[\]_]/g, (char, at: number) => {
		if (char !== '_') {
			return `\\${char}`;
		}
		const inWord =
			/[\p{L}\p{N}]/u.test(text[at - 1] ?? '') &&
			/[\p{L}\p{N}]/u.test(text[at + 1] ?? '');
		return inWord ? '_' : '\\_';
	});
}
